import { CommonModule } from '@angular/common';
import { Component, OnInit, OnDestroy } from '@angular/core';
import {
  ReactiveFormsModule,
  FormGroup,
  FormBuilder,
  FormControl,
  Validators,
  FormArray,
} from '@angular/forms';
import { RouterModule, Router } from '@angular/router';
import { MessageHandlerService } from '@core/services/message-handler/message-handler.service';
import { InputModule } from '@sharedModule/components/input/input.module';
import { ATTRIBUTES_CONSTANT } from '@sharedModule/constants/fields.constant';
import { MaterialModule } from '@sharedModule/material/material.module';
import { showValidationMsg } from '@sharedModule/utils/form.utils';
import { Subject, takeUntil } from 'rxjs';
import { PERMISSION_TYPE } from '../../constant/upsert-permission.constant';
import { PermissionService } from '../../services/permission.service';
import { PermissionsInterface } from '../../types/permission-list';
import {
  AddPermissionInterface,
  UpdatePermissionInterface,
} from '../../types/upsert-permission';

@Component({
  selector: 'app-upsert-permission',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    MaterialModule,
    InputModule,
    ReactiveFormsModule,
  ],
  templateUrl: './upsert-permission.component.html',
  styleUrl: './upsert-permission.component.scss',
})
export class UpsertPermissionComponent implements OnInit, OnDestroy {
  public readonly attrConstant = ATTRIBUTES_CONSTANT.permissions;
  public readonly permissionType = PERMISSION_TYPE;
  upsertPermissionFrom!: FormGroup;
  isEdit = false;
  #destroy$ = new Subject<void>();

  constructor(
    private router: Router,
    private fb: FormBuilder,
    private permissionService: PermissionService,
    private messageHandlerService: MessageHandlerService
  ) {}

  ngOnInit(): void {
    this.upsertPermissionFromInit();
    this.isEdit = this._isEdit(this.router.url);
  }

  upsertPermissionFromInit(): void {
    this.upsertPermissionFrom = new FormGroup({
      permission_section_id: new FormControl(''),
      [this.attrConstant.name.key]: new FormControl('', [Validators.required]),
      [this.attrConstant.description.key]: new FormControl('', [
        Validators.required,
      ]),
      [this.attrConstant.selectionOrder.key]: new FormControl('', [
        Validators.required,
      ]),
      [this.attrConstant.isCollapsible.key]: new FormControl(false),
      [this.attrConstant.isActive.key]: new FormControl(true),
      permissions: new FormArray([this.getPermissionsData()]),
    });
  }

  getPermissionsData(): FormGroup {
    return this.fb.group({
      [this.attrConstant.permData.title.key]: new FormControl('', [
        Validators.required,
      ]),
      [this.attrConstant.permData.slug.key]: new FormControl('', [
        Validators.required,
      ]),
      [this.attrConstant.permData.permissionType.key]: new FormControl('', [
        Validators.required,
      ]),
      [this.attrConstant.permData.description.key]: new FormControl(''),
      [this.attrConstant.permData.apiEndpoint.key]: new FormControl('', [
        Validators.required,
      ]),
      [this.attrConstant.permData.displayInMenu.key]: new FormControl(false),
      [this.attrConstant.permData.isActive.key]: new FormControl(false),
    });
  }

  addPermissionRow(): void {
    this.permissionData.push(this.getPermissionsData());
  }

  removePermissionRow(i: number): void {
    this.permissionData.removeAt(i);
  }

  get permissionData(): FormArray {
    return this.upsertPermissionFrom.controls['permissions'] as FormArray;
  }

  private _isEdit = (url: string): boolean => {
    const endUrl: string[] = url.split('/');
    if (endUrl[3] == 'edit') {
      this.getPermissionDetails(endUrl[4]);
      return true;
    } else {
      return false;
    }
  };

  getPermissionDetails(permissionSectionId: string): void {
    this.permissionService
      .getPermissionDetails({
        permission_section_id: permissionSectionId,
      })
      .pipe(takeUntil(this.#destroy$))
      .subscribe({
        next: x => {
          if (x.success) {
            this.upsertPermissionFrom.patchValue({
              permission_section_id: x.response.permission_section_id,
              [this.attrConstant.name.key]: x.response.name,
              [this.attrConstant.description.key]: x.response.description,
              [this.attrConstant.selectionOrder.key]:
                x.response.selection_order,
              [this.attrConstant.isCollapsible.key]: x.response.is_collapsible,
              [this.attrConstant.isActive.key]: x.response.is_active,
            });
            this.patchValueToFormArray(x.response.permissions);
          }
        },
      });
  }

  patchValueToFormArray(permissions: PermissionsInterface[]): void {
    const control = this.upsertPermissionFrom.controls[
      'permissions'
    ] as FormArray;
    control.removeAt(0);
    const permData = this.attrConstant.permData;
    for (const key in permissions) {
      control.push(
        this.fb.group({
          permission_id: permissions[key].permission_id,
          [permData.title.key]: new FormControl(permissions[key].title, [
            Validators.required,
          ]),
          [permData.slug.key]: new FormControl(permissions[key].slug, [
            Validators.required,
          ]),
          [permData.permissionType.key]: new FormControl(
            permissions[key].permission_type,
            [Validators.required]
          ),
          [permData.description.key]: new FormControl(
            permissions[key].description
          ),
          [permData.apiEndpoint.key]: new FormControl(
            permissions[key].api_endpoint,
            [Validators.required]
          ),
          [permData.displayInMenu.key]: new FormControl(
            permissions[key].display_in_menu
          ),
          [permData.isActive.key]: new FormControl(permissions[key].is_active),
        })
      );
    }
  }

  onUpsertPermission(): void {
    showValidationMsg(this.upsertPermissionFrom);
    if (this.upsertPermissionFrom.valid) {
      if (this.isEdit) this.updatePermission(this.upsertPermissionFrom.value);
      else this.addPermission(this.upsertPermissionFrom.value);
    }
  }

  addPermission(formValue: AddPermissionInterface): void {
    this.permissionService
      .addPermission(formValue)
      .pipe(takeUntil(this.#destroy$))
      .subscribe({
        next: x => {
          if (x.success) {
            this.messageHandlerService.show(x.message);
            this.router.navigate(['/user-management/permission/list']);
          }
        },
      });
  }

  updatePermission(formValue: UpdatePermissionInterface): void {
    this.permissionService
      .updatePermission(formValue)
      .pipe(takeUntil(this.#destroy$))
      .subscribe({
        next: x => {
          if (x.success) {
            this.messageHandlerService.show(x.message);
            this.router.navigate(['/user-management/permission/list']);
          }
        },
      });
  }

  ngOnDestroy(): void {
    this.#destroy$.next();
    this.#destroy$.complete();
  }
}
